import {
    ApplicationRef,
    Inject,
    Injectable,
    InjectionToken,
    OnDestroy,
    inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AncViewportService, isAncViewportBelow } from '@libs/common-interceptor';
import { BehaviorSubject, Observable, Subject, race, timer } from 'rxjs';
import { distinctUntilChanged, filter, first, map, takeUntil } from 'rxjs/operators';
import { Access_Functions as PERMISSIONS } from '../constants/permissions.constant';
import { customerType } from '../enums/customer-type.enum';
import { hasOrderTypeList } from '../utils/order-counts-section.util';

import {
    MenuAction,
    MenuItem,
    MenuTabGroup,
    MenuViewModel
} from './menu.models';

export interface MenuPermissionsPort {
    getPermission(permission?: string): unknown;
}

export interface MenuStoragePort {
    get(key: string): any;
}

interface LogoImageStorage {
    readonly logo_image?: string;
    readonly preference_setting?: readonly {
        readonly name?: string;
        readonly value?: string;
    }[];
}

export interface MenuDataTransferPort {
    globalSearch$: Observable<{ flag?: boolean }>;
    globalsearches(payload: { Data: string; flag: boolean }): void;
    updateSecondaryMenuCount$?: Observable<string>;
    /** Pushed by the Address Book grid with its list total — it has no count endpoint. */
    addressBookCount$?: Observable<number>;
    sendenableflags?(payload: { flag: unknown }): void;
    sendSellerStoreIdData?(payload: { sellusellar_store_id: unknown }): void;
}

export interface MenuAnalyticsPort {
    trackEvent(eventName: string): void;
}

export interface MenuAuthBridgePort {
    openRuleEngine(userDetails: MenuAuthUserDetails, config: MenuAuthConfig): void;
    cleanup(): void;
}

export interface MenuGetterSetterPort {
    rtl: boolean;
}

export interface MenuDashboardPort {
    isControlTower(): boolean;
}

export interface MenuCountPorts {
    productService?: any;
    orderService?: any;
    poService?: any;
    supplierService?: any;
    reportsService?: any;
    productConfigurationService?: any;
    addressBookService?: any;
}

export interface MenuAuthUserDetails {
    tenant_id?: number;
    tenant_name?: string;
    tenant_email?: string;
    customer_id?: number;
    customer_name?: string;
    customer_email?: string;
    target_system?: 'OXM' | 'WMS' | 'OMS';
}

export interface MenuAuthConfig {
    logo: string;
    logoAlt: string;
    appName: string;
    tenantName: string;
    locale: string;
    preferences: Record<string, string>;
    labels: Record<string, string>;
}

export const MENU_PERMISSIONS = new InjectionToken<MenuPermissionsPort>(
    'MENU_PERMISSIONS'
);
export const MENU_STORAGE = new InjectionToken<MenuStoragePort>('MENU_STORAGE');
export const MENU_DATA_TRANSFER = new InjectionToken<MenuDataTransferPort>(
    'MENU_DATA_TRANSFER'
);
export const MENU_ANALYTICS = new InjectionToken<MenuAnalyticsPort>(
    'MENU_ANALYTICS'
);
export const MENU_AUTH_BRIDGE = new InjectionToken<MenuAuthBridgePort>(
    'MENU_AUTH_BRIDGE'
);
export const MENU_GETTER_SETTER = new InjectionToken<MenuGetterSetterPort>(
    'MENU_GETTER_SETTER'
);
export const MENU_DASHBOARD = new InjectionToken<MenuDashboardPort>(
    'MENU_DASHBOARD'
);
export const MENU_COUNT_PORTS = new InjectionToken<MenuCountPorts>(
    'MENU_COUNT_PORTS'
);

@Injectable({ providedIn: 'root' })
export class MenuFacadeService implements OnDestroy {
    /** Maps first URL segment to primary menu item id when they differ. */
    private static readonly ROUTE_SEGMENT_TO_MODULE_ID: Readonly<
        Record<string, string>
    > = {
        order: 'orders',
        accounts: 'self-services'
    };

    private static readonly MARKETPLACE_ECOMMERCE_GROUP_IDS: readonly string[] = [
        'orders-ecommerce-group-connected',
        'orders-ecommerce-group-disconnected'
    ];

    private readonly destroy$ = new Subject<void>();
    private readonly expandedIds = new Set<string>();
    private readonly orderSubMenuOverrides = new Map<string, readonly MenuItem[]>();
    private readonly counts = new Map<string, number>();
    private productMarketplaceChildren: readonly MenuItem[] = [];
    private marketplaceOrderChildren: readonly MenuItem[] = [];
    private currentFirst = '';
    private currentPath = '';
    private isGlobalSearch = false;

    /**
     * Responsive nav (OEM-7023, FR-010): below 1200px the sidebar is an icon rail that
     * opens as an overlay drawer ("expanded" = drawer open). The desktop collapse
     * preference is remembered while on a tablet and restored at 1200px and wider.
     */
    private readonly viewport = inject(AncViewportService);
    private isTablet = this.viewport.isTabletOrBelow();
    private desktopCollapsed = false;
    private keepDrawerOnNextNavigation = false;
    private isCollapsed = this.isTablet;

    private readonly viewModelSubject = new BehaviorSubject<MenuViewModel>(
        this.emptyViewModel()
    );
    readonly viewModel$ = this.viewModelSubject.asObservable();

    constructor(
        private readonly router: Router,
        private readonly appRef: ApplicationRef,
        @Inject(MENU_STORAGE) private readonly storage: MenuStoragePort,
        @Inject(MENU_PERMISSIONS)
        private readonly permissions: MenuPermissionsPort,
        @Inject(MENU_DATA_TRANSFER)
        private readonly dataTransfer: MenuDataTransferPort,
        @Inject(MENU_ANALYTICS) private readonly analytics: MenuAnalyticsPort,
        @Inject(MENU_AUTH_BRIDGE)
        private readonly authBridge: MenuAuthBridgePort,
        @Inject(MENU_GETTER_SETTER)
        private readonly getterSetter: MenuGetterSetterPort,
        @Inject(MENU_DASHBOARD) private readonly dashboard: MenuDashboardPort,
        @Inject(MENU_COUNT_PORTS) private readonly countPorts: MenuCountPorts
    ) {
        this.syncRouteState(this.router.url);
        this.emitViewModel();

        this.viewport.viewportClass$
            .pipe(
                map((viewportClass) => isAncViewportBelow(viewportClass, 'laptop')),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((isTablet) => this.onTabletChange(isTablet));

        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd
                ),
                takeUntil(this.destroy$)
            )
            .subscribe((event) => {
                this.syncRouteState(event.urlAfterRedirects);
                // Any navigation (back/forward, in-page links) closes the tablet drawer,
                // except one started by opening a group, so its submenu stays reachable.
                if (this.keepDrawerOnNextNavigation) {
                    this.keepDrawerOnNextNavigation = false;
                } else {
                    this.closeTabletDrawer();
                }
                this.emitViewModel();
            });

        this.dataTransfer.globalSearch$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data) => {
                this.isGlobalSearch = !!data?.flag;
            });

        this.dataTransfer.updateSecondaryMenuCount$
            ?.pipe(takeUntil(this.destroy$))
            .subscribe((moduleName) => {
                if (!moduleName) return;
                this.refreshCountsForModule(String(moduleName));
            });

        this.dataTransfer.addressBookCount$
            ?.pipe(takeUntil(this.destroy$))
            .subscribe((count) => {
                this.counts.set('suppliers-address-book', count || 0);
                this.emitViewModel();
            });

        this.scheduleCountLoad();
    }

    /**
     * Sidebar badge counts are nine separate endpoints — each with its own CORS
     * preflight — and they were fired straight from this constructor, i.e. while
     * the routed screen was still fetching its own data.
     *
     * They wait for the app's first stable tick instead: that is the point where
     * the route's own in-flight requests have settled, so the badges no longer
     * compete with the screen for connections. requestIdleCallback was tried first
     * and is not equivalent — on a slow bundle the main thread goes idle waiting on
     * the network, so it fired at ~700 ms against a 7 s first paint.
     *
     * The timer is not a rare fallback — measured on this app, isStable never emits
     * true, because something keeps a recurring task inside the zone. So 2s is the
     * real schedule in practice, and it is chosen to sit after first paint (0.9s on
     * an optimized build, #4972 §9) without leaving the badges visibly blank. If the
     * zone is ever quietened, isStable wins the race and they arrive sooner.
     */
    private scheduleCountLoad(): void {
        race(
            this.appRef.isStable.pipe(first((stable) => stable)),
            timer(2000).pipe(map(() => true))
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.loadCounts());
    }

    /** Icon class (e.g. `icon-box-copy`) for the active primary sidebar module. */
    getActivePrimaryModuleIcon(): string | undefined {
        const viewModel = this.viewModelSubject.value;
        const activeId = viewModel.activeItemId;

        if (!activeId) {
            return undefined;
        }

        return viewModel.items.find((item) => item.id === activeId)?.icon;
    }

    /**
     * Sidebar destination for a breadcrumb label (primary/secondary/tab items).
     * Prefer this over raw route-tree prefixes, which often 404.
     */
    resolveRouteByLabel(label: string): string | undefined {
        if (!label) {
            return undefined;
        }

        return this.findMenuRouteByLabel(
            label,
            this.viewModelSubject.value.items
        );
    }

    private findMenuRouteByLabel(
        label: string,
        items: readonly MenuItem[]
    ): string | undefined {
        for (const item of items) {
            if (item.label === label && item.route) {
                return item.route;
            }

            const fromChildren = this.findMenuRouteByLabel(
                label,
                item.children || []
            );
            if (fromChildren) {
                return fromChildren;
            }

            for (const group of item.tabGroups || []) {
                const fromTabs = this.findMenuRouteByLabel(
                    label,
                    group.items || []
                );
                if (fromTabs) {
                    return fromTabs;
                }
            }
        }

        return undefined;
    }

    handleAction(action: MenuAction): void {
        if (action.type === 'toggle-collapse') {
            this.toggleCollapsed();
            return;
        }

        if (action.type === 'toggle-group' && action.itemId) {
            const wasExpanded = this.expandedIds.has(action.itemId);
            const navigateToSection =
                wasExpanded &&
                !!action.route &&
                !this.isCurrentPathUnderMenuItem(action.itemId, action.route);

            if (navigateToSection) {
                // Already expanded but user switched from another section — navigate, do not collapse
                this.expandedIds.add(action.itemId);
                this.keepDrawerOnNextNavigation = true;
                this.navigateToRoute(action.route, action.itemId);
            } else {
                this.toggleGroup(action.itemId);

                // Navigate only when expanding; collapsing must not re-open via expandPathToItem
                if (action.route && !wasExpanded) {
                    this.keepDrawerOnNextNavigation = true;
                    this.navigateToRoute(action.route, action.itemId);
                }
            }

            return;
        }

        if (action.type === 'open-rule-engine') {
            this.openRuleEngine();
            return;
        }

        if (!action.route) return;

        // Leaf selection closes the tablet drawer. Group toggles keep it open so the
        // submenu that just expanded stays reachable.
        this.closeTabletDrawer();
        this.navigateToRoute(action.route, action.itemId);
    }

    loadCounts(): void {
        if (this.hasOrdersMenuAccess()) {
            this.loadOrderCounts();
            this.loadOnlineOrderCounts();
            this.loadProblemOrderCounts();
        }

        this.loadProductModuleCounts();

        if (this.hasInboundMenuAccess()) {
            this.loadInboundCounts();
        }
        if (this.hasReportMenuAccess()) {
            this.loadReportCounts();
        }
        if (this.hasSupplierMenuAccess()) {
            this.loadSupplierCounts();
            this.loadAddressBookCount();
        }

        if (this.hasIntegrationMenuAccess()) {
            this.loadIntegrationCounts();
        }
    }

    /** Loads inventory / marketplace counts only when the user can see Products in the menu. */
    private loadProductModuleCounts(): void {
        if (this.usesProductConfigurationRoutes()) {
            if (this.hasProductConfigurationBulkCountAccess()) {
                this.loadProductConfigurationCounts();
            }
            if (this.hasProductMarketplaceCountAccess()) {
                this.loadProductMarketplaceCounts();
            }
            return;
        }

        if (this.isDpdLogistic()) {
            return;
        }

        if (this.hasStandardInventoryCountAccess()) {
            this.loadProductCounts();
        }
        if (this.hasProductMarketplaceCountAccess()) {
            this.loadProductMarketplaceCounts();
        }
        if (
            this.hasPermission(
                PERMISSIONS.INVENTORY_BRAND_READ,
                PERMISSIONS.INVENTORY_BRAND_WRITE,
                PERMISSIONS.INVENTORY_PRODUCT_READ,
                PERMISSIONS.INVENTORY_PRODUCT_WRITE
            )
        ) {
            this.loadBrandCount();
        }
    }

    private refreshCountsForModule(moduleName: string): void {
        const normalized = moduleName.toLowerCase();

        switch (normalized) {
            case 'orders':
                if (this.hasOrdersMenuAccess()) {
                    this.loadOrderCounts();
                    this.loadOnlineOrderCounts();
                    this.loadProblemOrderCounts();
                }
                break;
            case 'inventory':
                this.loadProductModuleCounts();
                break;
            case 'inbound':
            case 'po':
                if (this.hasInboundMenuAccess()) {
                    this.loadInboundCounts();
                }
                break;
            case 'reports':
                if (this.hasReportMenuAccess()) {
                    this.loadReportCounts();
                }
                break;
            case 'suppliers':
                if (this.hasSupplierMenuAccess()) {
                    this.loadSupplierCounts();
                }
                break;
            case 'products':
                if (this.hasProductConfigurationBulkCountAccess()) {
                    this.loadProductConfigurationCounts();
                }
                break;
            case 'integrations':
                if (this.hasIntegrationMenuAccess()) {
                    this.loadIntegrationCounts();
                }
                break;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.authBridge.cleanup();
    }

    private emptyViewModel(): MenuViewModel {
        return {
            logoAlt: 'Logo',
            items: [],
            isRtl: false,
            isCollapsed: false
        };
    }

    private emitViewModel(): void {
        this.viewModelSubject.next(this.createViewModel());
    }

    private createViewModel(): MenuViewModel {
        const logo = this.storage.get('LogoImage');
        const userData = this.storage.get('userData');

        return {
            logoUrl: this.resolveCustomLogoUrl(logo),
            logoAlt: userData?.customer_name || 'Logo',
            isRtl: !!this.getterSetter.rtl,
            isCollapsed: this.isCollapsed,
            activeItemId: this.currentFirst,
            items: this.buildItems()
        };
    }

    private buildItems(): readonly MenuItem[] {
        if (this.isAsendiaUKCustomer()) {
            return this.buildAsendiaUKItems().filter((item) => item.state !== 'disabled');
        }

        return this.buildStandardCustomerItems().filter((item) => item.state !== 'disabled');
    }

    /**
     * Asendia UK primary menu: Dashboard, Orders, Products (configuration), Configurations.
     * Matches legacy `buildAsendiaUKCustomerMenu` + `appendProductConfig`.
     */
    private buildAsendiaUKItems(): MenuItem[] {
        const items: MenuItem[] = [
            this.buildMenuItem('dashboard', 'Dashboard', 'icon-grid', '/dashboard', [
                PERMISSIONS.CUSTOMER_FULFILMENT_GRAPH_READ,
                PERMISSIONS.CUSTOMER_SHIPMENT_GRAPH_READ
            ]),
            this.buildMenuItem(
                'orders',
                'Orders',
                'icon-layers',
                this.defaultOrderUrl(),
                [
                    PERMISSIONS.DRAFT_ORDER_READ,
                    PERMISSIONS.CONFIRMED_ORDER_READ,
                    PERMISSIONS.SUCCESSFUL_ORDER_READ,
                    PERMISSIONS.RETURN_ORDER_READ,
                    PERMISSIONS.ORDER_PARCEL_READ,
                    PERMISSIONS.MARKETPLACE_ORDER_READ,
                    PERMISSIONS.ECOMMERCE_ORDER_READ,
                    PERMISSIONS.ORDER_ACTION_REQUIRED_READ,
                    PERMISSIONS.ORDER_BULK_ACTIVITY_READ
                ],
                this.buildOrderChildren()
            )
        ];

        const productConfigurationItem = this.buildProductConfigurationPrimaryItem();
        if (productConfigurationItem) {
            items.push(productConfigurationItem);
        }

        items.push(
            this.buildMenuItem(
                'integration',
                'Configurations',
                'icon-cpu',
                this.defaultIntegrationUrl(),
                this.getConfigPermissions(),
                this.buildIntegrationChildren()
            )
        );

        const selfServicesItem = this.buildSelfServicesPrimaryItem();
        if (selfServicesItem) {
            items.push(selfServicesItem);
        }

        return items;
    }

    /**
     * Default customer menu. DPD omits standard Inventory Products and inserts
     * Products (configuration) before Report — matches legacy `buildOtherCustomerMenu`.
     */
    private buildStandardCustomerItems(): MenuItem[] {
        const items: MenuItem[] = [
            this.buildMenuItem('dashboard', 'Dashboard', 'icon-grid', '/dashboard', [
                PERMISSIONS.CUSTOMER_FULFILMENT_GRAPH_READ,
                PERMISSIONS.CUSTOMER_SHIPMENT_GRAPH_READ
            ])
        ];

        if (!this.isDpdLogistic()) {
            items.push(
                this.buildMenuItem(
                    'products',
                    'Products',
                    'icon-box-copy',
                    this.defaultProductUrl(),
                    [
                        PERMISSIONS.INVENTORY_PRODUCT_READ,
                        PERMISSIONS.KIT_PRODUCT_READ,
                        PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ,
                        PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_READ,
                        PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ
                    ],
                    this.buildProductChildren()
                )
            );
        }

        items.push(
            this.buildMenuItem(
                'supplier',
                'Supplier',
                'icon-link',
                this.defaultSupplierUrl(),
                [
                    PERMISSIONS.MANAGE_SUPPLIER_READ,
                    PERMISSIONS.SUPPLIER_BULK_ACTIVITY_READ
                ],
                this.buildSupplierChildren()
            ),
            this.buildMenuItem(
                'orders',
                'Orders',
                'icon-layers',
                this.defaultOrderUrl(),
                [
                    PERMISSIONS.DRAFT_ORDER_READ,
                    PERMISSIONS.CONFIRMED_ORDER_READ,
                    PERMISSIONS.SUCCESSFUL_ORDER_READ,
                    PERMISSIONS.RETURN_ORDER_READ,
                    PERMISSIONS.ORDER_PARCEL_READ,
                    PERMISSIONS.MARKETPLACE_ORDER_READ,
                    PERMISSIONS.ECOMMERCE_ORDER_READ,
                    PERMISSIONS.ORDER_ACTION_REQUIRED_READ,
                    PERMISSIONS.ORDER_BULK_ACTIVITY_READ
                ],
                this.buildOrderChildren()
            ),
            this.buildMenuItem(
                'purchase-orders',
                'Inbound',
                'icon-clipboard',
                this.defaultInboundUrl(),
                [
                    PERMISSIONS.INBOUND_CONSIGNMENT_READ,
                    PERMISSIONS.INBOUND_GOODS_RECEIVED_READ,
                    PERMISSIONS.INBOUND_BULK_ACTIVITY_READ
                ],
                this.buildInboundChildren()
            )
        );

        const productConfigurationItem = this.buildProductConfigurationPrimaryItem();
        if (productConfigurationItem && this.isDpdLogistic()) {
            const reportIndex = items.findIndex((item) => item.id === 'report');
            const insertAt = reportIndex >= 0 ? reportIndex : items.length;
            items.splice(insertAt, 0, productConfigurationItem);
        }

        items.push(
            this.buildMenuItem(
                'report',
                'Report',
                'icon-pie-chart',
                this.defaultReportUrl(),
                [PERMISSIONS.MANAGE_REPORT_READ],
                this.buildReportChildren()
            ),
            this.buildMenuItem(
                'integration',
                'Configurations',
                this.isDpdLogistic() ? 'icon-Configurations' : 'icon-cpu',
                this.defaultIntegrationUrl(),
                this.getConfigPermissions(),
                this.buildIntegrationChildren()
            ),
            {
                id: 'rule-engine',
                label: 'Rule Engine',
                icon: 'icon-zap',
                type: 'primary',
                state: 'default',
                customClass: 'new-sidebar-menu__item--rule-engine',
                metadata: {
                    specialAction: 'rule-engine',
                    displayName: 'Rule Engine'
                }
            }
        );

        const selfServicesItem = this.buildSelfServicesPrimaryItem();
        if (selfServicesItem) {
            const ruleEngineIndex = items.findIndex((item) => item.id === 'rule-engine');
            const insertAt = ruleEngineIndex >= 0 ? ruleEngineIndex : items.length;
            items.splice(insertAt, 0, selfServicesItem);
        }

        return items;
    }

    private buildSelfServicesPrimaryItem(): MenuItem | null {
        if (this.isDpdLogistic()) {
            return null;
        }

        const children = this.buildSelfServicesChildren();
        if (!children.length) {
            return null;
        }

        return this.buildMenuItem(
            'self-services',
            'Self Services',
            'icon-settings',
            this.defaultSelfServiceUrl(),
            this.getSelfServicesPermissions(),
            children
        );
    }

    private buildSelfServicesChildren(): readonly MenuItem[] {
        return [
            this.buildChild(
                'self-services-my-information',
                'My Information',
                'icon-user',
                '/accounts/account-overview',
                false,
                [],
                [
                    PERMISSIONS.MY_INFORMATION_READ,
                    PERMISSIONS.MY_INFORMATION_WRITE
                ]
            ),
            this.buildChild(
                'self-services-user-role',
                'User Role Management',
                'icon-users',
                '/accounts/user-role',
                false,
                [],
                [
                    PERMISSIONS.CUSTOMER_USER_MANAGEMENT_READ,
                    PERMISSIONS.CUSTOMER_USER_MANAGEMENT_WRITE
                ]
            ),
            this.buildChild(
                'self-services-dispatch-note',
                'Dispatch Note',
                'icon-clipboard',
                '/accounts/dispatch-note',
                false,
                [],
                [
                    PERMISSIONS.DISPATCH_NOTE_READ,
                    PERMISSIONS.DISPATCH_NOTE_WRITE
                ]
            ),
            ...(this.isSsoConfigurationAvailable()
                ? [
                      this.buildChild(
                          'self-services-sso-configuration',
                          'SSO Configuration',
                          'icon-settings',
                          '/accounts/sso-configuration',
                          false,
                          [],
                          [PERMISSIONS.SSO_CONFIG_READ, PERMISSIONS.SSO_CONFIG_WRITE]
                      )
                  ]
                : [])
        ].filter((child) => child.state !== 'disabled');
    }

    /**
     * SSO is hidden for DPD and only offered when both the tenant and the
     * customer have the feature switched on (legacy secondary-menu gate).
     */
    private isSsoConfigurationAvailable(): boolean {
        if (this.isDpdLogistic()) {
            return false;
        }
        const userData = this.storage.get('userData');
        return (
            this.parseBooleanFlag(userData?.is_tenant_sso_feature) &&
            this.parseBooleanFlag(userData?.is_customer_sso_feature)
        );
    }

    private parseBooleanFlag(value: unknown): boolean {
        return value === true || value === 1 || value === '1' || value === 'true';
    }

    private getSelfServicesPermissions(): readonly string[] {
        return [
            PERMISSIONS.MY_INFORMATION_READ,
            PERMISSIONS.MY_INFORMATION_WRITE,
            PERMISSIONS.CUSTOMER_USER_MANAGEMENT_READ,
            PERMISSIONS.CUSTOMER_USER_MANAGEMENT_WRITE,
            PERMISSIONS.DISPATCH_NOTE_READ,
            PERMISSIONS.DISPATCH_NOTE_WRITE
        ];
    }

    private defaultSelfServiceUrl(): string | undefined {
        if (this.hasPermission(
            PERMISSIONS.MY_INFORMATION_READ,
            PERMISSIONS.MY_INFORMATION_WRITE
        )) {
            return '/accounts/account-overview';
        }

        if (this.hasPermission(
            PERMISSIONS.CUSTOMER_USER_MANAGEMENT_READ,
            PERMISSIONS.CUSTOMER_USER_MANAGEMENT_WRITE
        )) {
            return '/accounts/user-role';
        }

        if (this.hasPermission(
            PERMISSIONS.DISPATCH_NOTE_READ,
            PERMISSIONS.DISPATCH_NOTE_WRITE
        )) {
            return '/accounts/dispatch-note';
        }

        return undefined;
    }

    /**
     * Products primary for Asendia UK / DPD — routes under `/products-configuration/*`
     * (legacy `appendProductConfig` + `handleProductBookModuleList`).
     */
    private buildProductConfigurationPrimaryItem(): MenuItem | null {
        if (!this.isAsendiaUKCustomer() && !this.isDpdLogistic()) {
            return null;
        }

        if (!this.hasProductConfigurationEligibility()) {
            return null;
        }

        return this.buildMenuItem(
            'products-configuration',
            'Products',
            'icon-box-copy',
            this.defaultProductConfigurationUrl(),
            [
                PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ,
                PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_WRITE
            ],
            this.buildProductConfigurationChildren()
        );
    }

    private hasProductConfigurationEligibility(): boolean {
        return this.hasPermission(
            PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ,
            PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_WRITE,
            PERMISSIONS.INVENTORY_PRODUCT_READ,
            PERMISSIONS.INVENTORY_PRODUCT_WRITE
        );
    }

    private usesProductConfigurationRoutes(): boolean {
        return (
            (this.isAsendiaUKCustomer() || this.isDpdLogistic()) &&
            this.hasProductConfigurationEligibility()
        );
    }

    private buildMenuItem(
        id: string,
        label: string,
        icon: string,
        route: string | undefined,
        permissions: readonly (string | undefined)[],
        children: readonly MenuItem[] = []
    ): MenuItem {
        const hasPermission = permissions.some((permission) =>
            !!this.permissions.getPermission(permission)
        );

        return {
            id,
            label,
            icon,
            route,
            type: 'primary',
            state: hasPermission ? this.getState(route) : 'disabled',
            isExpanded: this.expandedIds.has(id),
            customClass: `new-sidebar-menu__item--${id}`,
            children,
            metadata: { displayName: label, moduleName: id }
        };
    }

    private buildProductChildren(): readonly MenuItem[] {
        return [
            this.buildChild('products-products', 'Inventory Products', 'icon-store', '/products/products', true, [], [
                PERMISSIONS.INVENTORY_PRODUCT_READ
            ]),
            this.buildChild('products-kit-products', 'Kit Products', 'icon-store', '/products/kit-products/all', true, [], [
                PERMISSIONS.KIT_PRODUCT_READ
            ]),
            this.buildChild(
                'products-marketplaces',
                'Channel Products',
                'icon-home',
                '/products/marketplaces/all/all-products',
                true,
                [],
                [PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ]
            ),
            this.buildChild('products-unsync', 'Unsync Product', 'icon-loader', '/products/unsync', false, [], [
                PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_READ
            ]),
            this.buildChild('products-brand-management', 'Brand Management', 'icon-store', '/products/brand-management', true, [], [
                PERMISSIONS.INVENTORY_BRAND_READ,
                PERMISSIONS.INVENTORY_BRAND_WRITE,
                PERMISSIONS.INVENTORY_PRODUCT_READ,
                PERMISSIONS.INVENTORY_PRODUCT_WRITE
            ]),
            this.buildChild('products-bulkactivity', 'Bulk Activities', 'icon-Bulk-Upload', '/products/bulkactivity', true, [], [
                PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ
            ])
        ].filter((child) => child.state !== 'disabled');
    }

    /** Secondary items for `/products-configuration/*` (legacy `handleProductBookModuleList`). */
    private buildProductConfigurationChildren(): readonly MenuItem[] {
        return [
            this.buildChild(
                'products-configuration-marketplaces',
                'Channel Products',
                'icon-home',
                '/products-configuration/marketplaces/all/all',
                true,
                [],
                [
                    PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ,
                    PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_WRITE,
                    PERMISSIONS.INVENTORY_PRODUCT_READ,
                    PERMISSIONS.INVENTORY_PRODUCT_WRITE
                ]
            ),
            this.buildChild(
                'products-configuration-bulkactivity',
                'Bulk Activities',
                'icon-Bulk-Upload',
                '/products-configuration/bulkactivity',
                true,
                [],
                [
                    PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ,
                    PERMISSIONS.INVENTORY_BULK_ACTIVITY_WRITE
                ]
            ),
            this.buildChild(
                'products-configuration-unsync',
                'Product Issues',
                'icon-loader',
                '/products-configuration/unsync',
                false,
                [],
                [
                    PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_READ,
                    PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_WRITE
                ]
            )
        ].filter((child) => child.state !== 'disabled');
    }

    private buildOrderChildren(): readonly MenuItem[] {
        const children: MenuItem[] = [];

        if (this.hasParcelTrackPermission()) {
            children.push(
                this.buildChild(
                    'orders-parcel',
                    'Track Your Parcel',
                    'icon-icon-dis-gift-a-product',
                    '/order/parcel/all',
                    false,
                    [],
                    [PERMISSIONS.ORDER_PARCEL_READ]
                )
            );
        }

        children.push(
            this.buildChild(
                'orders-draft',
                'Draft',
                'icon-check',
                '/order/draft',
                true,
                [],
                [PERMISSIONS.DRAFT_ORDER_READ]
            ),
            this.buildChild(
                'orders-confirmed',
                this.getConfirmedOrderLabel(),
                'icon-check',
                '/order/confirmed',
                true,
                [],
                [
                    PERMISSIONS.CONFIRMED_ORDER_READ,
                    PERMISSIONS.SUCCESSFUL_ORDER_READ
                ]
            ),
            this.buildChild(
                'orders-return',
                'Return Orders',
                'icon-arrow-left',
                '/order/return',
                true,
                [],
                [PERMISSIONS.RETURN_ORDER_READ]
            ),
            this.buildChild(
                'orders-ecommerce',
                'E-Commerce Orders',
                'icon-Ecommerce-Order',
                this.defaultEcommerceOrderUrl(),
                true,
                [],
                [
                    PERMISSIONS.ECOMMERCE_ORDER_READ,
                    PERMISSIONS.MARKETPLACE_ORDER_READ
                ]
            ),
            this.buildChild(
                'orders-problem',
                'Action Required',
                'icon-Problem-Orders',
                '/order/problem-orders',
                true,
                [],
                [PERMISSIONS.ORDER_ACTION_REQUIRED_READ]
            ),
            this.buildChild(
                'orders-blocked',
                'Blocked Orders',
                'icon-icon-dis-gift-a-product',
                '/order/blocked-order',
                false,
                [],
                [
                    PERMISSIONS.ORDER_BLOCK_ORDERS_READ,
                    PERMISSIONS.ORDER_BLOCK_ORDERS_WRITE
                ]
            ),
            this.buildChild(
                'orders-bulkactivity',
                'Bulk Activities',
                'icon-Bulk-Upload',
                '/order/bulkactivity',
                true,
                [],
                [PERMISSIONS.ORDER_BULK_ACTIVITY_READ]
            )
        );

        return children
            .map((child) => this.enrichOrderChild(child))
            .filter((child) => child.state !== 'disabled');
    }

    private buildSupplierChildren(): readonly MenuItem[] {
        return [
            this.buildChild(
                'suppliers-manage',
                'Manage Suppliers',
                'icon-store',
                '/supplier/supplier',
                true,
                [],
                [PERMISSIONS.MANAGE_SUPPLIER_READ]
            ),
            this.buildChild(
                'suppliers-address-book',
                'Address Book',
                'icon-map-pin',
                '/supplier/address-book',
                true,
                [],
                []
            ),
            this.buildChild(
                'suppliers-bulkactivity',
                'Bulk Activities',
                'icon-Bulk-Upload',
                '/supplier/bulkactivity',
                true,
                [],
                [PERMISSIONS.SUPPLIER_BULK_ACTIVITY_READ]
            )
        ].filter((child) => child.state !== 'disabled');
    }

    private buildInboundChildren(): readonly MenuItem[] {
        return [
            this.buildChild(
                'inbound-consignment',
                'Inbound Consignment',
                'icon-store',
                '/purchase-orders/purchase-orders',
                true,
                [],
                [PERMISSIONS.INBOUND_CONSIGNMENT_READ]
            ),
            this.buildChild(
                'inbound-grn',
                'Goods Received',
                'icon-store',
                '/purchase-orders/grn-orders',
                false,
                [],
                [PERMISSIONS.INBOUND_GOODS_RECEIVED_READ]
            ),
            this.buildChild(
                'inbound-bulkactivity',
                'Bulk Activities',
                'icon-Bulk-Upload',
                '/purchase-orders/bulkactivity',
                true,
                [],
                [PERMISSIONS.INBOUND_BULK_ACTIVITY_READ]
            )
        ].filter((child) => child.state !== 'disabled');
    }

    private buildReportChildren(): readonly MenuItem[] {
        return [
            this.buildChild(
                'reports-manage',
                'Fulfillment Reports',
                'icon-pie-chart',
                '/report/report',
                true,
                [],
                [PERMISSIONS.MANAGE_REPORT_READ]
            ),
            // OEM-6688 AC-1.1: Online Channels Reports — a second, independent
            // submodule alongside Fulfillment Reports above.
            this.buildChild(
                'reports-online-channel',
                'Online Channels Reports',
                'icon-pie-chart',
                '/report/online-channel-report',
                true,
                [],
                [PERMISSIONS.MANAGE_REPORT_READ]
            )
        ].filter((child) => child.state !== 'disabled');
    }

    private buildIntegrationChildren(): readonly MenuItem[] {
        const children: MenuItem[] = [
            this.buildChild(
                'integration-marketplaces',
                'eCommerce',
                'icon-Ecommerce',
                '/integration/marketplaces',
                true,
                [],
                [
                    PERMISSIONS.INTEGRATION_MARKETPLACE_READ,
                    PERMISSIONS.CUSTOMER_CONFIG_ECOMMERCE_READ
                ]
            ),
            this.buildChild(
                'integration-carrier',
                'Carrier & Store Management',
                'icon-truck',
                '/integration/carrier-management',
                false,
                [],
                [
                    PERMISSIONS.CUSTOMER_CONFIG_CARRIER_STORE_READ
                ]
            ),
            this.buildChild(
                'integration-third-party-logistics',
                '3rd Party Logistics',
                'icon-truck',
                '/integration/third-party-logistics',
                false,
                [],
                [
                    PERMISSIONS.THIRD_PARTY_LOGISTIC_READ
                ]
            ),
            ...(this.isAsendiaConnectCustomer()
                ? []
                : [
                      this.buildChild(
                          'integration-service-mapping',
                          'Service Mapping',
                          'icon-Order-Mapping',
                          '/integration/service-mapping',
                          false,
                          [],
                          [
                              PERMISSIONS.CUSTOMER_CONFIG_SERVICE_MAPPING_READ,
                              // JDL seller: no SERVICE_MAPPING_* on the role (OEM-7398)
                              PERMISSIONS.SERVICE_ONLY_MAPPING
                          ]
                      )
                  ]),
            this.buildChild(
                'integration-printer-setup',
                'Printer Setup',
                'icon-printer',
                '/accounts/setup-printer',
                false,
                [],
                [
                    PERMISSIONS.CUSTOMER_CONFIG_PRINTER_SETUP_READ
                ]
            ),
            this.buildChild(
                'integration-default-parameter',
                'Default Parameter',
                'icon-settings',
                '/integration/default-parameter',
                false,
                [],
                [
                    PERMISSIONS.CUSTOMER_CONFIG_DEFAULT_PARAMS_READ
                ]
            )
        ];

        if (this.isDpdLogistic() && this.isPackingSlipEnabled()) {
            children.push(
                this.buildChild(
                    'integration-packing-slip',
                    'Packing Slip',
                    'icon-settings',
                    '/integration/packing-list',
                    false
                )
            );
        }

        // JDL: Pick List settings (the packing list screen), with ORDER_PICK_LIST only
        children.push(
            this.buildChild(
                'integration-pick-list',
                'Pick List',
                'icon-settings',
                '/integration/pick-list',
                false,
                [],
                [PERMISSIONS.ORDER_PICK_LIST]
            )
        );

        children.push(
            this.buildChild(
                'integration-label-format',
                'Label Format Setup',
                'icon-printer',
                '/integration/label-format',
                false,
                [],
                [
                    PERMISSIONS.CUSTOMER_CONFIG_LABEL_FORMAT_READ
                ]
            ),
            this.buildChild(
                'integration-bulkactivity',
                'Bulk Activities',
                'icon-Bulk-Upload',
                '/integration/bulkactivity',
                false,
                [],
                [
                    PERMISSIONS.CUSTOMER_CONFIG_BULK_ACTIVITY_READ
                ]
            )
        );

        return children.filter((child) => child.state !== 'disabled');
    }

    /** Primary Configurations visibility — legacy `getConfigPermissions`. */
    private getConfigPermissions(): readonly (string | undefined)[] {
        return [
            PERMISSIONS.INTEGRATION_MARKETPLACE_READ,
            PERMISSIONS.CUSTOMER_CONFIG_CARRIER_STORE_READ,
            PERMISSIONS.CUSTOMER_CONFIG_BULK_ACTIVITY_READ,
            PERMISSIONS.CUSTOMER_CONFIG_ECOMMERCE_READ,
            PERMISSIONS.CUSTOMER_CONFIG_SERVICE_MAPPING_READ,
            PERMISSIONS.CUSTOMER_CONFIG_PRINTER_SETUP_READ,
            PERMISSIONS.CUSTOMER_CONFIG_DEFAULT_PARAMS_READ,
            PERMISSIONS.CUSTOMER_CONFIG_LABEL_FORMAT_READ
        ];
    }

    private buildChild(
        id: string,
        label: string,
        icon: string,
        route: string,
        showCount = false,
        tabGroups: readonly MenuTabGroup[] = [],
        permissions: readonly (string | undefined)[] = [],
        children: readonly MenuItem[] = []
    ): MenuItem {
        const hasPermission =
            !permissions.length ||
            permissions.some((permission) =>
                !!this.permissions.getPermission(permission)
            );

        const normalizedChildren = this.refreshChildStates(children);

        return {
            id,
            label,
            icon,
            route,
            type: 'secondary',
            state: hasPermission
                ? this.getSecondaryChildState(id, route)
                : 'disabled',
            showCount,
            count: this.counts.get(id) || 0,
            isExpanded: this.expandedIds.has(id),
            children: normalizedChildren,
            tabGroups,
            customClass: `new-sidebar-menu__child--${id}`,
            metadata: { displayName: label }
        };
    }

    private loadOrderCounts(): void {
        this.countPorts.orderService?.getOrdersCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes;
                if (!counts) return;
                this.counts.set('orders-draft', counts.draft?.count || 0);
                this.counts.set('orders-confirmed', counts.confirmed?.count || 0);
                this.counts.set('orders-return', counts.return_orders || 0);
                this.counts.set('orders-bulkactivity', counts.bulk_orders || counts.bulk_count || 0);
                this.applyOrderSubMenus(counts);
                this.emitViewModel();
            });
    }

    private loadProblemOrderCounts(): void {
        if (
            !this.permissions.getPermission(PERMISSIONS.ORDER_ACTION_REQUIRED_READ) &&
            !this.permissions.getPermission(PERMISSIONS.ORDER_ACTION_REQUIRED_WRITE)
        ) {
            return;
        }

        this.countPorts.orderService?.getProblemOrderCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (response?.result !== 100 || !response?.data) return;
                const counts = response?.data?.attributes ?? response?.data;
                this.counts.set('orders-problem', counts?.problem_orders || 0);
                this.emitViewModel();
            });
    }

    private loadOnlineOrderCounts(): void {
        if (
            !this.usesFlatEcommerceOrderRoutes() &&
            !this.hasOnlineOrderCountAccess()
        ) {
            return;
        }

        if (this.usesFlatEcommerceOrderRoutes()) {
            this.loadAsendiaUkMarketplaceOrderCounts();
            return;
        }

        this.countPorts.orderService?.getOnlineOrdersCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const attributes = response?.data?.attributes;
                if (!attributes) return;
                this.counts.set('orders-ecommerce', attributes.total_order_count || 0);
                this.marketplaceOrderChildren = this.buildMarketplaceOrderTree(
                    attributes.connectedMarketplace || [],
                    attributes.disconnectedMarketplace || []
                );
                this.emitViewModel();
            });
    }

    /** Asendia UK uses flat marketplace routes and `marketplace_counts` API. */
    private loadAsendiaUkMarketplaceOrderCounts(): void {
        this.countPorts.orderService?.getMarketplacesOrderCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const attributes = response?.data?.attributes;
                if (!attributes) return;
                this.counts.set('orders-ecommerce', attributes.total_order_count || 0);
                this.marketplaceOrderChildren = [];
                this.emitViewModel();
            });
    }

    private loadProductCounts(): void {
        this.countPorts.productService?.getInventoryCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes || response?.data;
                if (!counts) return;
                this.counts.set('products-products', counts.product || 0);
                this.counts.set('products-kit-products', counts.kit_product || 0);
                this.counts.set('products-bulkactivity', counts.bulk_count || 0);
                this.emitViewModel();
            });
    }

    /**
     * Brands have no count endpoint — legacy read `meta.total_count` off a
     * one-row page of `products/brands`, so this does the same.
     */
    private loadBrandCount(): void {
        this.countPorts.productService
            ?.getGridData?.('products/brands?page[number]=1&page[size]=1')
            ?.pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.counts.set(
                        'products-brand-management',
                        response?.result === 100
                            ? response?.meta?.total_count || 0
                            : 0
                    );
                    this.emitViewModel();
                },
                error: () => {
                    this.counts.set('products-brand-management', 0);
                    this.emitViewModel();
                }
            });
    }

    private loadProductMarketplaceCounts(): void {
        this.countPorts.productService?.getMarketplacesPdtCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes;
                if (!counts) return;
                const marketplacesKey = this.usesProductConfigurationRoutes()
                    ? 'products-configuration-marketplaces'
                    : 'products-marketplaces';

                this.counts.set(
                    marketplacesKey,
                    counts.connected_product_count || 0
                );
                this.emitViewModel();
            });
    }

    private loadInboundCounts(): void {
        this.countPorts.poService?.getPOCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes || response?.data;
                if (!counts) return;
                this.counts.set('inbound-consignment', counts.purchase_orders || 0);
                this.counts.set('inbound-bulkactivity', counts.bulk_orders || 0);
                this.emitViewModel();
            });
    }

    private loadReportCounts(): void {
        this.countPorts.reportsService?.getReportCounts?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes || response?.data;
                if (!counts) return;
                this.counts.set('reports-manage', counts.report_counts || 0);
                this.counts.set('reports-online-channel', counts.online_channel_counts || 0);
                this.emitViewModel();
            });
    }

    /**
     * Address books have no count endpoint — legacy read `meta.total_count` off
     * a one-row page, and the grid refreshes the badge via `addressBookCount$`.
     */
    private loadAddressBookCount(): void {
        this.countPorts.addressBookService
            ?.getCount?.()
            ?.pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (response?.result !== 100) return;
                    this.counts.set(
                        'suppliers-address-book',
                        response?.meta?.total_count || 0
                    );
                    this.emitViewModel();
                },
                error: () => undefined
            });
    }

    private loadSupplierCounts(): void {
        this.countPorts.supplierService?.getSupplierCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes || response?.data;
                if (!counts) return;
                this.counts.set('suppliers-manage', counts.total_count || 0);
                this.counts.set('suppliers-bulkactivity', counts.bulk_count || 0);
                this.emitViewModel();
            });
    }

    private loadProductConfigurationCounts(): void {
        this.countPorts.productConfigurationService?.getInventoryCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes || response?.data;
                if (!counts) return;
                this.counts.set('products-configuration-bulkactivity', counts.bulk_count || 0);
                this.emitViewModel();
            });
    }

    /** eCommerce marketplace count — legacy `handleIntegrationsModuleCounts`. */
    private loadIntegrationCounts(): void {
        this.countPorts.orderService?.getMarketplaceCount?.()
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const counts = response?.data?.attributes ?? response?.data;
                if (!counts) return;
                this.counts.set(
                    'integration-marketplaces',
                    counts.total_marketplaces || 0
                );
                this.emitViewModel();
            });
    }

    /**
     * Matches legacy secondary-menu getDisplayText: Asendia Connect and
     * Asendia UK see "Orders/Shipments"; everyone else sees "Successful Orders".
     */
    private getConfirmedOrderLabel(): string {
        if (this.isAsendiaUKCustomer() || this.isAsendiaUser()) {
            return 'Orders/Shipments';
        }

        return 'Successful Orders';
    }

    private buildMarketplaceOrderTree(
        connectedMarketplaces: readonly any[],
        disconnectedMarketplaces: readonly any[]
    ): readonly MenuItem[] {
        if (this.usesFlatEcommerceOrderRoutes()) {
            return [];
        }

        const groups: MenuItem[] = [];

        if (connectedMarketplaces.length) {
            groups.push(
                this.buildMarketplaceOrderGroup(
                    'connected',
                    'Connected',
                    connectedMarketplaces
                )
            );
        }

        if (disconnectedMarketplaces.length) {
            groups.push(
                this.buildMarketplaceOrderGroup(
                    'disconnected',
                    'Disconnected',
                    disconnectedMarketplaces
                )
            );
        }

        return groups;
    }

    private buildMarketplaceOrderGroup(
        groupType: string,
        label: string,
        marketplaces: readonly any[]
    ): MenuItem {
        const children = marketplaces
            .filter((marketplace) => !!marketplace?.source)
            .map((marketplace) =>
                this.mapMarketplaceOrderLeaf(marketplace, groupType)
            );

        const groupRoute = this.getMarketplaceGroupRoute(groupType);

        return {
            id: `orders-ecommerce-group-${groupType}`,
            label,
            route: groupRoute,
            type: 'secondary',
            state: this.getMarketplaceGroupState(groupType),
            showCount: true,
            count: children.reduce((total, item) => total + (item.count || 0), 0),
            isExpanded: this.expandedIds.has(`orders-ecommerce-group-${groupType}`),
            customClass: `new-sidebar-menu__nested-child--group new-sidebar-menu__nested-child--${groupType}`,
            children
        };
    }

    private mapMarketplaceOrderLeaf(
        marketplace: Record<string, any>,
        groupType: string
    ): MenuItem {
        const source = String(marketplace.source);
        const route = `/order/marketplaces/${groupType}/${source}/all`;

        return {
            id: `orders-ecommerce-${groupType}-${source}`,
            label: source,
            route,
            type: 'secondary',
            state: this.getMarketplaceOrderLeafState(groupType, source),
            icon: marketplace.delivery_only === true ? 'icon-package' : undefined,
            count: marketplace.order_count || 0,
            showCount: true,
            customClass: [
                `new-sidebar-menu__nested-child--marketplace`,
                `new-sidebar-menu__nested-child--${groupType}`,
                marketplace.delivery_only
                    ? 'new-sidebar-menu__nested-child--delivery-only'
                    : ''
            ].filter(Boolean),
            metadata: {
                displayName: source,
                deliveryOnly: marketplace.delivery_only,
                enableDraftOrders: marketplace.enable_draft_orders,
                sellerStoreId: marketplace.sellusellar_store_id
            }
        };
    }

    private mapProductMarketplaceChildren(
        marketplaces: any[],
        useProductConfigurationRoutes = false
    ): readonly MenuItem[] {
        const idPrefix = useProductConfigurationRoutes
            ? 'products-configuration-marketplaces'
            : 'products-marketplaces';
        const routePrefix = useProductConfigurationRoutes
            ? '/products-configuration/marketplaces'
            : '/products/marketplaces';

        return marketplaces
            .filter((marketplace) => !!marketplace?.source)
            .map((marketplace) => {
                const id = `${idPrefix}-${marketplace.seller_marketplace_id}`;
                const route = `${routePrefix}/${marketplace.seller_marketplace_id}`;

                return {
                    id,
                    label: marketplace.source,
                    route,
                    type: 'secondary' as const,
                    state: this.getChildState(route),
                    count: marketplace.product_count || 0,
                    showCount: true,
                    customClass: 'new-sidebar-menu__nested-child--marketplace',
                    metadata: {
                        displayName: marketplace.source,
                        enableDraftOrders: marketplace.enable_draft_orders,
                        sellerStoreId: marketplace.sellusellar_store_id
                    }
                };
            });
    }

    private refreshChildStates(children: readonly MenuItem[]): readonly MenuItem[] {
        return children.map((child) => {
            const nestedChildren = this.refreshChildStates(child.children || []);
            const marketplaceGroupType =
                this.getMarketplaceEcommerceGroupType(child.id);
            const marketplaceLeaf = this.getMarketplaceOrderLeafContext(child.id);
            const state =
                child.state === 'disabled'
                    ? 'disabled'
                    : marketplaceLeaf
                      ? this.getMarketplaceOrderLeafState(
                            marketplaceLeaf.groupType,
                            marketplaceLeaf.source
                        )
                      : marketplaceGroupType
                        ? this.getMarketplaceGroupState(marketplaceGroupType)
                        : this.getSecondaryChildState(child.id, child.route);

            return {
                ...child,
                state,
                isExpanded: this.expandedIds.has(child.id),
                children: nestedChildren
            };
        });
    }

    private getState(route?: string): 'default' | 'active' {
        return this.getActiveModuleId(route || '') === this.currentFirst
            ? 'active'
            : 'default';
    }

    private getChildState(route?: string): 'default' | 'active' {
        const targetPath = this.normalizePath(route || '');

        return targetPath &&
            (this.currentPath === targetPath ||
                this.currentPath.startsWith(`${targetPath}/`))
            ? 'active'
            : 'default';
    }

    /**
     * Active state for secondary sidebar items. Channel Products and E-Commerce
     * stay highlighted for any marketplace channel or inventory tab route.
     */
    private getSecondaryChildState(
        childId: string,
        route?: string
    ): 'default' | 'active' {
        if (
            childId === 'orders-ecommerce' &&
            this.isMarketplaceOrderPath(this.currentPath)
        ) {
            return 'active';
        }

        if (
            childId === 'products-marketplaces' &&
            this.isProductsMarketplacePath(this.currentPath)
        ) {
            return 'active';
        }

        if (
            childId === 'products-configuration-marketplaces' &&
            this.isProductConfigurationMarketplacePath(this.currentPath)
        ) {
            return 'active';
        }

        if (
            childId === 'products-kit-products' &&
            this.isKitProductsPath(this.currentPath)
        ) {
            return 'active';
        }

        return this.getChildState(route);
    }

    /**
     * Active when any marketplace order route belongs to this group (Connected / Disconnected).
     */
    private getMarketplaceGroupState(groupType: string): 'default' | 'active' {
        if (!this.isMarketplaceOrderPath(this.currentPath)) {
            return 'default';
        }

        const segments = this.currentPath.split('/').filter(Boolean);

        return segments[2] === groupType ? 'active' : 'default';
    }

    private getMarketplaceEcommerceGroupType(itemId: string): string | undefined {
        const prefix = 'orders-ecommerce-group-';

        if (!itemId.startsWith(prefix)) {
            return undefined;
        }

        return itemId.slice(prefix.length);
    }

    /**
     * Active when the current URL targets this marketplace under Connected / Disconnected.
     */
    private getMarketplaceOrderLeafState(
        groupType: string,
        source: string
    ): 'default' | 'active' {
        if (!this.isMarketplaceOrderPath(this.currentPath)) {
            return 'default';
        }

        const segments = this.currentPath.split('/').filter(Boolean);

        if (
            segments.length < 4 ||
            segments[0] !== 'order' ||
            segments[1] !== 'marketplaces' ||
            segments[2] !== groupType
        ) {
            return 'default';
        }

        const pathSource = segments[3];

        if (pathSource === 'all-orders') {
            return 'default';
        }

        return this.marketplaceSourcesMatch(pathSource, source)
            ? 'active'
            : 'default';
    }

    private getMarketplaceOrderLeafContext(
        itemId: string
    ): { readonly groupType: string; readonly source: string } | undefined {
        const match = /^orders-ecommerce-(connected|disconnected)-(.+)$/.exec(
            itemId
        );

        if (!match) {
            return undefined;
        }

        return {
            groupType: match[1],
            source: match[2]
        };
    }

    private marketplaceSourcesMatch(
        pathSource: string,
        menuSource: string
    ): boolean {
        return (
            this.normalizeMarketplaceSource(pathSource) ===
            this.normalizeMarketplaceSource(menuSource)
        );
    }

    private normalizeMarketplaceSource(source: string): string {
        return decodeURIComponent(source).toLowerCase().replace(/[_ ]/g, '-');
    }

    private defaultProductUrl(): string | undefined {
        if (this.hasPermission(PERMISSIONS.INVENTORY_PRODUCT_READ)) return '/products/products';
        if (this.hasPermission(PERMISSIONS.KIT_PRODUCT_READ)) return '/products/kit-products/all';
        if (this.hasPermission(PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ)) return '/products/bulkactivity';
        if (this.hasPermission(PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ)) {
            return '/products/marketplaces/all/all-products';
        }
        if (this.hasPermission(PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_READ)) return '/products/unsync';
        return undefined;
    }

    private defaultProductConfigurationUrl(): string | undefined {
        if (this.hasPermission(PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ)) {
            return '/products-configuration/marketplaces/all';
        }
        if (this.hasPermission(PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ)) {
            return '/products-configuration/bulkactivity';
        }
        if (this.hasPermission(PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_READ)) {
            return '/products-configuration/unsync';
        }
        return undefined;
    }

    private defaultOrderUrl(): string | undefined {
        const permissionRoutes: ReadonlyArray<{
            readonly allowed: boolean;
            readonly route: string;
        }> = [
            {
                allowed: this.hasParcelTrackPermission(),
                route: '/order/parcel/all'
            },
            {
                allowed: this.hasPermission(PERMISSIONS.DRAFT_ORDER_READ),
                route: '/order/draft'
            },
            {
                allowed: this.hasPermission(
                    PERMISSIONS.CONFIRMED_ORDER_READ,
                    PERMISSIONS.SUCCESSFUL_ORDER_READ
                ),
                route: this.getConfirmedDefaultRoute()
            },
            {
                allowed: this.hasPermission(PERMISSIONS.RETURN_ORDER_READ),
                route: '/order/return'
            },
            {
                allowed: this.hasPermission(
                    PERMISSIONS.MARKETPLACE_ORDER_READ,
                    PERMISSIONS.ECOMMERCE_ORDER_READ
                ),
                route: this.defaultEcommerceOrderUrl()
            },
            {
                allowed: this.hasPermission(PERMISSIONS.ORDER_ACTION_REQUIRED_READ),
                route: '/order/problem-orders'
            },
            {
                allowed: this.hasPermission(PERMISSIONS.ORDER_BULK_ACTIVITY_READ),
                route: '/order/bulkactivity'
            }
        ];

        return permissionRoutes.find((entry) => entry.allowed)?.route;
    }

    /**
     * List route for Connected / Disconnected marketplace groups (matches orders routing).
     */
    private getMarketplaceGroupRoute(groupType: string): string {
        const statusTab = this.getDefaultEcommerceMarketplaceStatusTab();

        return `/order/marketplaces/${groupType}/all-orders/all/${statusTab}`;
    }

    /**
     * DPD Transshipment: first status tab is Pick → `created` (see orders.constant mapRoutes).
     */
    private getDefaultEcommerceMarketplaceStatusTab(): string {
        if (this.usesDpdMarketplaceStatusTabs()) {
            return 'created';
        }

        return 'all';
    }

    private usesDpdMarketplaceStatusTabs(): boolean {
        if (!this.isDpdLogistic()) {
            return false;
        }

        const customerTypeId = this.storage.get('customerTypeId');

        return customerTypeId === customerType.Transshipment;
    }

    private defaultEcommerceOrderUrl(): string {
        if (this.usesFlatEcommerceOrderRoutes()) {
            return '/order/marketplaces/all-orders/all';
        }

        if (this.isDpdLogistic()) {
            return this.getMarketplaceGroupRoute('connected');
        }

        return this.getMarketplaceGroupRoute('connected');
    }

    private getConfirmedDefaultRoute(): string {
        if (this.hasParcelTrackPermission() && this.isAsendiaUKCustomer()) {
            return '/order/confirmed/all-confirmed/all/new';
        }

        if (this.isDpdLogistic()) {
            return this.getMarketplaceGroupRoute('connected');
        }

        if (this.isAsendiaUser()) {
            return '/order/confirmed/all-confirmed/all/all';
        }

        if (this.isAsendiaUKCustomer()) {
            return '/order/confirmed/all-confirmed/all/new';
        }

        return '/order/confirmed/all-confirmed/all/all';
    }

    private defaultSupplierUrl(): string | undefined {
        if (this.hasPermission(PERMISSIONS.MANAGE_SUPPLIER_READ)) {
            return '/supplier/supplier';
        }
        if (this.hasPermission(PERMISSIONS.SUPPLIER_BULK_ACTIVITY_READ)) {
            return '/supplier/bulkactivity';
        }
        return undefined;
    }

    private defaultInboundUrl(): string | undefined {
        if (this.hasPermission(PERMISSIONS.INBOUND_CONSIGNMENT_READ)) {
            return '/purchase-orders/purchase-orders';
        }
        if (this.hasPermission(PERMISSIONS.INBOUND_GOODS_RECEIVED_READ)) {
            return '/purchase-orders/grn-orders';
        }
        if (this.hasPermission(PERMISSIONS.INBOUND_BULK_ACTIVITY_READ)) {
            return '/purchase-orders/bulkactivity';
        }
        return undefined;
    }

    private defaultReportUrl(): string | undefined {
        if (this.hasPermission(PERMISSIONS.MANAGE_REPORT_READ)) {
            return '/report/report';
        }
        return undefined;
    }

    private defaultIntegrationUrl(): string | undefined {
        if (
            this.hasPermission(
                PERMISSIONS.INTEGRATION_MARKETPLACE_READ,
                PERMISSIONS.CUSTOMER_CONFIG_ECOMMERCE_READ
            )
        ) {
            return '/integration/marketplaces';
        }
        if (
            this.hasPermission(
                PERMISSIONS.CUSTOMER_CONFIG_CARRIER_STORE_READ
            )
        ) {
            return '/integration/carrier-management';
        }
        if (
            this.hasPermission(
                PERMISSIONS.THIRD_PARTY_LOGISTIC_READ
            )
        ) {
            return '/integration/third-party-logistics';
        }
        if (
            this.hasPermission(
                PERMISSIONS.CUSTOMER_CONFIG_SERVICE_MAPPING_READ
            )
        ) {
            return '/integration/service-mapping';
        }
        if (
            this.hasPermission(
                PERMISSIONS.CUSTOMER_CONFIG_DEFAULT_PARAMS_READ
            )
        ) {
            return '/integration/default-parameter';
        }
        if (
            this.hasPermission(
                PERMISSIONS.CUSTOMER_CONFIG_LABEL_FORMAT_READ
            )
        ) {
            return '/integration/label-format';
        }
        if (
            this.hasPermission(
                PERMISSIONS.CUSTOMER_CONFIG_BULK_ACTIVITY_READ
            )
        ) {
            return '/integration/bulkactivity';
        }
        if (
            this.hasPermission(
                PERMISSIONS.CUSTOMER_CONFIG_PRINTER_SETUP_READ
            )
        ) {
            return '/accounts/setup-printer';
        }
        if (this.isDpdLogistic()) {
            return '/integration/packing-list';
        }
        if (this.hasPermission(PERMISSIONS.ORDER_PICK_LIST)) {
            return '/integration/pick-list';
        }
        return undefined;
    }

    private hasPermission(...permissions: (string | undefined)[]): boolean {
        return permissions.some((permission) =>
            !!this.permissions.getPermission(permission)
        );
    }

    /** Matches primary Orders menu visibility (`buildStandardCustomerItems` / UK orders). */
    private hasOrdersMenuAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.DRAFT_ORDER_READ,
            PERMISSIONS.CONFIRMED_ORDER_READ,
            PERMISSIONS.SUCCESSFUL_ORDER_READ,
            PERMISSIONS.RETURN_ORDER_READ,
            PERMISSIONS.ORDER_PARCEL_READ,
            PERMISSIONS.MARKETPLACE_ORDER_READ,
            PERMISSIONS.ECOMMERCE_ORDER_READ,
            PERMISSIONS.ORDER_ACTION_REQUIRED_READ,
            PERMISSIONS.ORDER_BULK_ACTIVITY_READ
        );
    }

    private hasOnlineOrderCountAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.ECOMMERCE_ORDER_READ,
            PERMISSIONS.MARKETPLACE_ORDER_READ
        );
    }

    /** Standard `/products/*` inventory counts (products, kit, bulk). */
    private hasStandardInventoryCountAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.INVENTORY_PRODUCT_READ,
            PERMISSIONS.KIT_PRODUCT_READ,
            PERMISSIONS.INVENTORY_UNSYNC_PRODUCT_READ,
            PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ
        );
    }

    /** Channel / marketplace product counts (`products/marketplace_wise_count`). */
    private hasProductMarketplaceCountAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_READ,
            PERMISSIONS.INVENTORY_CHANNEL_PRODUCT_WRITE
        );
    }

    private hasProductConfigurationBulkCountAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.INVENTORY_BULK_ACTIVITY_READ,
            PERMISSIONS.INVENTORY_BULK_ACTIVITY_WRITE
        );
    }

    private hasSupplierMenuAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.MANAGE_SUPPLIER_READ,
            PERMISSIONS.SUPPLIER_BULK_ACTIVITY_READ
        );
    }

    private hasInboundMenuAccess(): boolean {
        return this.hasPermission(
            PERMISSIONS.INBOUND_CONSIGNMENT_READ,
            PERMISSIONS.INBOUND_GOODS_RECEIVED_READ,
            PERMISSIONS.INBOUND_BULK_ACTIVITY_READ
        );
    }

    private hasReportMenuAccess(): boolean {
        return this.hasPermission(PERMISSIONS.MANAGE_REPORT_READ);
    }

    private hasIntegrationMenuAccess(): boolean {
        return this.hasPermission(...this.getConfigPermissions());
    }

    private toggleGroup(itemId: string): void {
        if (this.expandedIds.has(itemId)) {
            this.collapseItemAndDescendants(itemId);
        } else {
            if (this.isPrimaryMenuItem(itemId)) {
                this.collapseOtherPrimaryGroups(itemId);
            }

            if (this.isMarketplaceEcommerceGroup(itemId)) {
                this.collapseSiblingMarketplaceGroups(itemId);
            }

            this.expandedIds.add(itemId);
        }
        this.emitViewModel();
    }

    private isPrimaryMenuItem(itemId: string): boolean {
        return this.viewModelSubject.value.items.some((item) => item.id === itemId);
    }

    /** Only one top-level primary group may be expanded at a time. */
    private collapseOtherPrimaryGroups(keepPrimaryId: string): void {
        for (const primaryItem of this.viewModelSubject.value.items) {
            if (primaryItem.id === keepPrimaryId) {
                continue;
            }

            this.collapseItemAndDescendants(primaryItem.id);
        }
    }

    private collapseItemAndDescendants(itemId: string): void {
        this.expandedIds.delete(itemId);

        const item = this.findMenuItem(itemId, this.viewModelSubject.value.items);
        if (!item?.children?.length) {
            return;
        }

        for (const child of item.children) {
            this.collapseItemAndDescendants(child.id);
        }
    }

    private isMarketplaceEcommerceGroup(itemId: string): boolean {
        return MenuFacadeService.MARKETPLACE_ECOMMERCE_GROUP_IDS.includes(itemId);
    }

    /** Only one of Connected / Disconnected may be expanded at a time. */
    private collapseSiblingMarketplaceGroups(keepItemId?: string): void {
        for (const groupId of MenuFacadeService.MARKETPLACE_ECOMMERCE_GROUP_IDS) {
            if (groupId !== keepItemId) {
                this.expandedIds.delete(groupId);
            }
        }
    }

    private toggleCollapsed(): void {
        this.isCollapsed = !this.isCollapsed;
        this.emitViewModel();
    }

    /** Crossing 1200px: rail on tablets, the remembered desktop state on wider screens. */
    private onTabletChange(isTablet: boolean): void {
        if (isTablet === this.isTablet) return;

        if (isTablet) {
            this.desktopCollapsed = this.isCollapsed;
            this.isCollapsed = true;
        } else {
            this.isCollapsed = this.desktopCollapsed;
        }
        this.isTablet = isTablet;
        this.emitViewModel();
    }

    /** Closes the overlay drawer on tablets; no effect on laptop/desktop. */
    private closeTabletDrawer(): void {
        if (!this.isTablet || this.isCollapsed) return;
        this.isCollapsed = true;
    }

    private resetGlobalSearch(): void {
        if (!this.isGlobalSearch) return;
        this.dataTransfer.globalsearches({ Data: '', flag: false });
        this.isGlobalSearch = false;
    }

    private applyItemMetadata(itemId?: string): void {
        const item = this.findMenuItem(itemId, this.viewModelSubject.value.items);
        if (!item?.metadata) return;

        if (item.metadata.enableDraftOrders !== undefined) {
            this.dataTransfer.sendenableflags?.({
                flag: item.metadata.enableDraftOrders
            });
        }

        if (item.metadata.sellerStoreId !== undefined) {
            this.dataTransfer.sendSellerStoreIdData?.({
                sellusellar_store_id: item.metadata.sellerStoreId
            });
        }
    }

    private findMenuItem(
        itemId: string | undefined,
        items: readonly MenuItem[]
    ): MenuItem | undefined {
        if (!itemId) return undefined;

        for (const item of items) {
            if (item.id === itemId) return item;

            const child = this.findMenuItem(itemId, item.children || []);
            if (child) return child;
        }

        return undefined;
    }

    private trackVisit(itemId?: string): void {
        if (!itemId) return;
        this.analytics.trackEvent(`OXM - ${itemId} menu visited`);
    }

    private openRuleEngine(): void {
        const userData = this.storage.get('userData');
        const userDetails: MenuAuthUserDetails = {
            tenant_id: userData?.tenant_id || this.storage.get('tenantId'),
            tenant_name: userData?.tenant_name || '',
            tenant_email: userData?.tenant_email || '',
            customer_id: userData?.customer_id || this.storage.get('customerId'),
            customer_name: userData?.customer_name || userData?.business_name || '',
            customer_email: userData?.email || '',
            target_system: 'OXM'
        };
        const config: MenuAuthConfig = {
            logo: this.storage.get('LogoImage')?.logo_image || 'assets/images/oxm-wordmark.svg',
            logoAlt: userDetails.customer_name || 'Logo',
            appName: userDetails.customer_name
                ? `${userDetails.customer_name} - Rule Platform`
                : 'Rule Platform',
            tenantName: userDetails.customer_name || '',
            locale: 'en',
            preferences: {},
            labels: {
                welcome: 'Welcome',
                dashboard: 'Dashboard',
                rules: 'Business Rules'
            }
        };

        if (userDetails.tenant_id && userDetails.customer_id) {
            this.authBridge.openRuleEngine(userDetails, config);
        }
    }

    private getFirstSegment(url: string): string {
        return this.normalizePath(url).split('/').filter(Boolean)[0] || '';
    }

    private getActiveModuleId(url: string): string {
        const path = this.normalizePath(url);

        if (path.startsWith('/accounts/setup-printer')) {
            return 'integration';
        }

        if (path.startsWith('/accounts/')) {
            return 'self-services';
        }

        const segment = this.getFirstSegment(url);

        return (
            MenuFacadeService.ROUTE_SEGMENT_TO_MODULE_ID[segment] ?? segment
        );
    }

    private syncRouteState(url: string): void {
        this.currentPath = this.normalizePath(url);
        this.currentFirst = this.getActiveModuleId(url);
        this.expandedIds.add(this.currentFirst);
        this.expandMarketplacePath(this.currentPath);
        this.expandProductMarketplacePath(this.currentPath);
        this.expandProductKitPath(this.currentPath);
        this.expandSelfServicesPath(this.currentPath);
    }

    /** Keeps Self Services expanded while browsing account-overview, user-role, or dispatch-note. */
    private expandSelfServicesPath(path: string): void {
        const normalized = this.normalizePath(path);

        if (
            normalized.startsWith('/accounts/account-overview') ||
            normalized.startsWith('/accounts/user-role') ||
            normalized.startsWith('/accounts/dispatch-note')
        ) {
            this.expandedIds.add('self-services');
        }
    }

    private normalizePath(url: string): string {
        const path = (url || '').split('?')[0].split('#')[0];

        return path.startsWith('/') ? path : `/${path}`;
    }

    private isExternalUrl(url: string): boolean {
        return url.startsWith('http://') || url.startsWith('https://');
    }

    private navigateToRoute(route: string, itemId?: string): void {
        this.resetGlobalSearch();
        this.applyItemMetadata(itemId);
        this.trackVisit(itemId);

        if (this.isExternalUrl(route)) {
            window.open(route, '_blank');
            return;
        }

        const normalizedRoute = this.normalizePath(route);
        this.syncRouteState(normalizedRoute);
        this.expandPathToItem(itemId);
        this.emitViewModel();
        void this.router.navigateByUrl(normalizedRoute);
    }

    private expandMarketplacePath(path: string): void {
        const segments = path.split('/').filter(Boolean);

        if (segments[0] !== 'order' || segments[1] !== 'marketplaces' || !segments[2]) {
            return;
        }

        this.expandedIds.add('orders');
        this.expandedIds.add('orders-ecommerce');

        const groupType = segments[2];
        if (groupType === 'connected' || groupType === 'disconnected') {
            const groupId = `orders-ecommerce-group-${groupType}`;
            this.collapseSiblingMarketplaceGroups(groupId);
            this.expandedIds.add(groupId);
        }
    }

    /** Keeps Products → Channel Products expanded while browsing any channel/tab. */
    private expandProductMarketplacePath(path: string): void {
        const segments = path.split('/').filter(Boolean);

        if (
            segments[0] === 'products' &&
            segments[1] === 'marketplaces'
        ) {
            this.expandedIds.add('products');
            this.expandedIds.add('products-marketplaces');
            return;
        }

        if (
            segments[0] === 'products-configuration' &&
            segments[1] === 'marketplaces'
        ) {
            this.expandedIds.add('products-configuration');
            this.expandedIds.add('products-configuration-marketplaces');
        }
    }

    private expandPathToItem(itemId?: string): void {
        if (!itemId) return;

        for (const primaryItem of this.viewModelSubject.value.items) {
            if (primaryItem.id === itemId) {
                return;
            }

            for (const child of primaryItem.children || []) {
                if (child.id === itemId) {
                    this.expandedIds.add(primaryItem.id);
                    this.expandedIds.add(itemId);
                    return;
                }

                for (const nested of child.children || []) {
                    if (nested.id === itemId) {
                        this.expandedIds.add(primaryItem.id);
                        this.expandedIds.add(child.id);
                        if (this.isMarketplaceEcommerceGroup(itemId)) {
                            this.collapseSiblingMarketplaceGroups(itemId);
                        }
                        this.expandedIds.add(itemId);
                        return;
                    }

                    if (nested.children?.some((leaf) => leaf.id === itemId)) {
                        this.expandedIds.add(primaryItem.id);
                        this.expandedIds.add(child.id);
                        if (this.isMarketplaceEcommerceGroup(nested.id)) {
                            this.collapseSiblingMarketplaceGroups(nested.id);
                        }
                        this.expandedIds.add(nested.id);
                        return;
                    }
                }
            }
        }
    }

    private enrichOrderChild(child: MenuItem): MenuItem {
        const state =
            child.state === 'disabled'
                ? 'disabled'
                : this.getSecondaryChildState(child.id, child.route);

        return {
            ...child,
            state,
            isExpanded: false,
            children: []
        };
    }

    private isMarketplaceOrderPath(path: string): boolean {
        return this.normalizePath(path).startsWith('/order/marketplaces');
    }

    private isProductConfigurationMarketplacePath(path: string): boolean {
        return this.normalizePath(path).startsWith('/products-configuration/marketplaces');
    }

    private isProductsMarketplacePath(path: string): boolean {
        return this.normalizePath(path).startsWith('/products/marketplaces');
    }

    private isKitProductsPath(path: string): boolean {
        const normalized = this.normalizePath(path);

        return (
            normalized === '/products/kit-products' ||
            normalized.startsWith('/products/kit-products/')
        );
    }

    /** Keeps Products → Kit Products expanded while browsing all / online / offline tabs. */
    private expandProductKitPath(path: string): void {
        const segments = path.split('/').filter(Boolean);

        if (segments[0] === 'products' && segments[1] === 'kit-products') {
            this.expandedIds.add('products');
            this.expandedIds.add('products-kit-products');
        }
    }

    /**
     * Whether the current URL belongs under this expandable menu item (vs another sibling).
     */
    private isCurrentPathUnderMenuItem(itemId: string, itemRoute: string): boolean {
        if (itemId === 'orders-ecommerce') {
            return this.isMarketplaceOrderPath(this.currentPath);
        }

        if (itemId === 'products-marketplaces') {
            return this.isProductsMarketplacePath(this.currentPath);
        }

        if (itemId === 'products-configuration-marketplaces') {
            return this.isProductConfigurationMarketplacePath(this.currentPath);
        }

        if (itemId === 'products-kit-products') {
            return this.isKitProductsPath(this.currentPath);
        }

        const targetPath = this.normalizePath(itemRoute);

        return (
            this.currentPath === targetPath ||
            this.currentPath.startsWith(`${targetPath}/`)
        );
    }

    private applyOrderSubMenus(counts: Record<string, any>): void {
        if (this.isDpdLogistic()) {
            return;
        }

        if (
            this.shouldShowConfirmedOrderSubmenu() &&
            hasOrderTypeList(counts.confirmed)
        ) {
            this.orderSubMenuOverrides.set(
                'orders-confirmed',
                this.mapOrderTypeList(counts.confirmed, 'confirmed')
            );
        } else {
            this.orderSubMenuOverrides.delete('orders-confirmed');
        }

        if (
            this.shouldShowDraftOrderSubmenu() &&
            hasOrderTypeList(counts.draft)
        ) {
            this.orderSubMenuOverrides.set(
                'orders-draft',
                this.mapOrderTypeList(counts.draft, 'draft')
            );
        } else {
            this.orderSubMenuOverrides.delete('orders-draft');
        }
    }

    private shouldShowConfirmedOrderSubmenu(): boolean {
        return !this.isDpdLogistic();
    }

    private shouldShowDraftOrderSubmenu(): boolean {
        return (
            !this.isDpdLogistic() &&
            this.isAsendiaUser() &&
            !this.isAsendiaUKCustomer()
        );
    }

    private mapOrderTypeList(
        section: Record<string, any> | undefined,
        sectionKey: string
    ): readonly MenuItem[] {
        if (!hasOrderTypeList(section)) {
            return [];
        }

        const list = section.list as Array<Record<string, any>>;

        return list.map((orderType) => {
            const orderTypeName = String(orderType.name || '').replace(/[_ ]/g, '-');
            const route = `/order/${sectionKey}/${orderTypeName}/${orderType.id}`;

            return {
                id: `orders-${sectionKey}-${orderType.id}`,
                label: String(orderType.name || '').replace(/_/g, '-'),
                route,
                type: 'secondary' as const,
                state: this.getChildState(route),
                showCount: true,
                count: orderType.count || 0,
                metadata: { displayName: String(orderType.name || '') }
            };
        });
    }

    private hasParcelTrackPermission(): boolean {
        return (
            this.hasPermission(PERMISSIONS.ORDER_PARCEL_READ) &&
            this.dashboard.isControlTower()
        );
    }

    private isAsendiaUKCustomer(): boolean {
        return this.getStorageFlag('isAsendiaUserUk');
    }

    private isAsendiaUser(): boolean {
        return this.getStorageFlag('isAsendiaUser');
    }

    private isDpdLogistic(): boolean {
        return this.getStorageFlag('isDpdLogistic');
    }

    /** Asendia Connect or Asendia UK customer — Service Mapping isn't relevant for them. */
    private isAsendiaConnectCustomer(): boolean {
        return this.isAsendiaUser() || this.isAsendiaUKCustomer();
    }

    private isPackingSlipEnabled(): boolean {
        const userData = this.storage.get('userData');
        return Number(userData?.is_packing_slip_enabled) === 1;
    }

    /**
     * Asendia UK uses `/order/marketplaces/:storeId/all/:status`.
     * DPD and standard customers use connected/disconnected group routes.
     */
    private usesFlatEcommerceOrderRoutes(): boolean {
        return this.isAsendiaUKCustomer() && !this.isDpdLogistic();
    }

    private getStorageFlag(key: string): boolean {
        const value = this.storage.get(key);

        return value === true || value === 'true';
    }

    private resolveCustomLogoUrl(
        logo: LogoImageStorage | null | undefined
    ): string | undefined {
        const fromImage = logo?.logo_image?.trim();
        if (fromImage) {
            return fromImage;
        }

        const fromPreference = this.getLogoPreferenceValue(logo)?.trim();
        return fromPreference || undefined;
    }

    private getLogoPreferenceValue(
        logo: LogoImageStorage | null | undefined
    ): string | undefined {
        const preference = logo?.preference_setting?.find(
            (entry) => entry?.name === 'logo' && entry?.value
        );

        return preference?.value?.trim() || undefined;
    }
}
